import { Interest } from 'src/types/graphql'

/**
 * Maps the page a visitor signed up from to the user's first-touch intent.
 * Drives initial HubSpot pipeline assignment + email sequence routing
 * during the auto-register path triggered by `currentUser`.
 *
 * Conservative by design: ambiguous pages (brand, contacto, profiles) return
 * undefined so we don't lock a contact into the wrong sequence on a guess.
 *
 * Accepts `router.pathname` (Next.js Pages Router file-based path with
 * dynamic segments still as `[param]`), not the URL pathname.
 */
export const inferInterest = (
  pathname: string | undefined
): Interest | undefined => {
  if (!pathname) return undefined

  // Host funnels: /anfitrion, /anfitrion/negocio, /anfitrion/broker,
  // /anfitrion/tradicional, /anfitrion/valua-tu-propiedad, /anfitrion/invita,
  // /anfitrion/blog. Anyone landing here is evaluating listing their space.
  if (pathname.startsWith('/anfitrion')) return Interest.Host

  // Renter pillar pages: /[pillar], /[pillar]/[city], /[pillar]/[city]/[hood],
  // /[pillar]/[city]/[hood]/[configId], /[pillar]/flotillas (B2B fleet —
  // renter intent, the broker pipeline auto-promotes later if volume warrants).
  if (pathname.startsWith('/[pillar]')) return Interest.Rent

  // Renter search + directory + sizing guide
  if (
    pathname === '/search' ||
    pathname.startsWith('/directory') ||
    pathname === '/guia-de-tamanos'
  ) {
    return Interest.Rent
  }

  // Home leans renter (browsing intent dominates host listing intent)
  if (pathname === '/') return Interest.Rent

  // Ambiguous: /contacto, /nosotros, /medios, /acerca-de-spotme, /business/*,
  // /user/*, auth + dashboard pages → no inference. Let downstream signals
  // (checkout, list-your-space) classify them.
  return undefined
}
